import { ReactNode } from 'react'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { styled, TextField as MuiTextField } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import ClearIcon from '@mui/icons-material/Clear'

interface TextInputProps<T extends FieldValues> extends UseControllerProps<T> {
	isDisabled?: boolean
	isRequired?: boolean
	label?: string
	onChange?: (value: string | undefined) => void
	placeholder?: string
	startAdornment?: ReactNode
	isClearable?: boolean
}

const ClearableTextInput = <T extends FieldValues>({
													   control,
													   isDisabled,
													   isRequired,
													   label,
													   name,
													   onChange,
													   placeholder,
													   rules,
													   startAdornment,
													   isClearable = true
												   }: TextInputProps<T>) => {
	const { formatMessage } = useIntl()

	const {
		field,
		fieldState: { error }
	} = useController({
		control,
		name,
		rules: {
			...rules,
			required: isRequired
		}
	})

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value

		field.onChange(value?.slice(0, rules?.maxLength as number))
	}

	const handleClear = () => {
		onChange?.(undefined)
		field.onChange('')
	}

	return (
		<TextField
			fullWidth
			label={label}
			value={field.value ?? ''}
			onChange={handleChange}
			placeholder={placeholder}
			disabled={isDisabled}
			error={!!error}
			helperText={
				error
					? formatMessage({ id: `global.errors.${error.type}` })
					: undefined
			}
			InputProps={{
				startAdornment: startAdornment ? (
					<InputAdornment position="start">{startAdornment}</InputAdornment>
				) : undefined,
				endAdornment:
					isClearable && field.value ? (
						<InputAdornment position="end">
							<IconButton
								size="small"
								onClick={handleClear}
								onMouseDown={(e) => e.preventDefault()}
								edge="end"
							>
								<ClearIcon fontSize="small" />
							</IconButton>
						</InputAdornment>
					) : undefined
			}}
		/>
	)
}

const TextField = styled(MuiTextField)({
	flex: 1
})

export default ClearableTextInput
